import { baseApi } from "./baseApi";
import type { ScopeFilterParams } from "../../../shared/types/accessScope";
import type { ApplicationFormConfig } from "../../../shared/api/applicationForm";
import type {
  ConsentConfig,
  JobPosting,
  JobsListResponse,
  JobAccessResponse,
} from "../../../shared/api/jobs";
import type { JobReachResponse } from "../../views/dashboard/jobs/jobReach";

export type {
  ApplicationFormFieldConfig,
  CustomApplicationField,
  ApplicationFormConfig,
} from "../../../shared/api/applicationForm";
export type {
  ConsentItem,
  ConsentConfig,
  JobPosting,
  JobsListResponse,
  JobAccessResponse,
} from "../../../shared/api/jobs";

export const DEFAULT_APPLICATION_FORM_CONFIG: ApplicationFormConfig = {
  resume: { visible: true, required: true },
  firstName: { visible: true, required: true },
  lastName: { visible: true, required: true },
  email: { visible: true, required: true },
  phone: { visible: true, required: false },
  linkedinUrl: { visible: true, required: false },
  videoCoverLetter: { visible: false, required: false },
};

export const DEFAULT_CONSENT_CONFIG: ConsentConfig = [
  {
    id: "privacy",
    label: "Privacy Policy",
    text: "I agree to the Privacy Policy and consent to the collection, processing, storage, and transfer (including cross-border) of my personal information for recruitment purposes. I understand my application may be evaluated using AI-assisted tools and reviewed by human recruiters before any final decision. I may request access, correction, or deletion of my data at any time by contacting .",
    type: "required",
  },
  {
    id: "data-retention",
    label: "Data Retention",
    text: "I consent to my information being retained for future opportunities in accordance with the Data Retention Policy.",
    type: "optional",
  },
  {
    id: "sms",
    label: "SMS/Calls",
    text: "I consent to receive SMS/calls regarding my application. Message and data rates may apply. Consent is not a condition of employment, and I may opt out at any time by replying STOP or contacting .",
    type: "optional",
  },
];

/**
 * The default set with carrier-ready SMS wording, for an instance whose
 * `sms_compliance` flag is on. Mirrors server/db/domains/jobs.ts.
 */
export const SMS_COMPLIANT_CONSENT_CONFIG: ConsentConfig = DEFAULT_CONSENT_CONFIG.map((item) =>
  item.id === "sms"
    ? {
        ...item,
        text: "I agree to receive text messages from Skyhire about my application, including updates and recruiter messages. Msg frequency varies. Msg & data rates may apply. Reply HELP for help, STOP to opt out. Consent is not a condition of employment. Contact: HR@skypoint.ai.",
      }
    : item,
);

/** Location, group and department are the scope filter's ids (D16). */
export interface JobsFilters extends ScopeFilterParams {
  status?: "all" | "published" | "draft" | "expired" | "internal";
  jobType?: string;
  hiringManager?: string;
  search?: string;
  searchMode?: "simple" | "boolean";
  page?: number;
  limit?: number;
}

export interface CreateJobRequest {
  title: string;
  /**
   * The title's entry in Settings > Job Titles, or null for a title typed in
   * freely. The server saves the entry's exact name.
   */
  jobTitleId?: string | null;
  titleEs?: string | null;
  description?: string | null;
  descriptionEs?: string | null;
  industry?: string | null;
  jobType?: string | null;
  /** on_site, remote or hybrid; "unspecified" when not set. */
  workplaceType?: string | null;
  department?: string | null;
  /**
   * The department's id (D24). Send it only when the user picked or cleared one;
   * omitted, the job keeps what it has. The server writes `department` from the label.
   */
  departmentId?: string | null;
  locationId?: number | null;
  locationName?: string | null;
  address?: string | null;
  city?: string | null;
  state?: string | null;
  stateAbbr?: string | null;
  zipCode?: string | null;
  salaryMin?: number | null;
  salaryMax?: number | null;
  salaryType?: string | null;
  salaryCurrency?: string | null;
  /**
   * Whether employees may refer for this job at all. Off keeps it out of the
   * employee referral hub even while published. Independent of the bonus.
   */
  referralsEnabled?: boolean;
  /** Referral bonus. Set by HR on the job; off means the job carries no reward. */
  referralBonusEnabled?: boolean;
  referralBonusAmount?: number | null;
  referralBonusCurrency?: string | null;
  requirements?: string[];
  benefits?: string[];
  applyMethod?: string | null;
  externalApplyUrl?: string | null;
  requireCandidateLogin?: boolean;
  postToIndeed?: boolean;
  applicationFormConfig?: ApplicationFormConfig | null;
  consentConfig?: ConsentConfig | null;
  isPublished?: boolean;
  expiresAt?: string | null;
  surveyTemplateId?: string | null;
  postingOwnerEmail?: string | null;
  hiringManagerEmail?: string | null;
  /** The recruiter running this requisition; the Recruiter role scopes on it. */
  recruiterEmail?: string | null;
}

export interface UpdateJobRequest extends Partial<CreateJobRequest> {
  id: string;
}

/** GET /jobs/:id/reach. Needs jobs.update. */
export interface JobReachArgs {
  jobId: string;
  instanceId: string;
  /** Someone about to be named on the job (recruiter, hiring manager). */
  email?: string;
  /** Where the job might move. "" means none; omitted means where it is now. */
  locationId?: number | "";
  /** Where the job might move. "" means none; omitted means where it is now. */
  departmentId?: string;
}

/**
 * Adding, removing or re-placing a job also changes the jobs-to-fix counts, each
 * department's job count, and whether the Department filter shows (D17).
 */
const JOB_PLACEMENT_TAGS = ["Jobs", "JobsToFix", "Departments", "AccessScope"] as const;

export const jobsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getJobs: builder.query<JobsListResponse, JobsFilters | void>({
      query: (filters) => {
        const params = new URLSearchParams();
        if (filters?.status) params.append("status", filters.status);
        if (filters?.locationId !== undefined) {
          params.append("locationId", String(filters.locationId));
        }
        if (filters?.groupId) params.append("groupId", filters.groupId);
        if (filters?.departmentId) params.append("departmentId", filters.departmentId);
        if (filters?.jobType) params.append("jobType", filters.jobType);
        if (filters?.hiringManager) params.append("hiringManager", filters.hiringManager);
        if (filters?.search) params.append("search", filters.search);
        if (filters?.searchMode) params.append("searchMode", filters.searchMode);
        if (filters?.page) params.append("page", String(filters.page));
        if (filters?.limit) params.append("limit", String(filters.limit));
        const queryString = params.toString();
        return `/jobs${queryString ? `?${queryString}` : ""}`;
      },
      providesTags: ["Jobs"],
    }),

    getJob: builder.query<JobPosting, string>({
      query: (id) => `/jobs/${id}`,
      providesTags: (_result, _error, id) => [{ type: "Jobs", id }],
    }),

    createJob: builder.mutation<JobPosting, CreateJobRequest>({
      query: (body) => ({
        url: "/jobs",
        method: "POST",
        body,
      }),
      invalidatesTags: [...JOB_PLACEMENT_TAGS],
    }),

    updateJob: builder.mutation<JobPosting, UpdateJobRequest>({
      query: ({ id, ...body }) => ({
        url: `/jobs/${id}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: (_result, _error, { id }) => [...JOB_PLACEMENT_TAGS, { type: "Jobs", id }],
    }),

    deleteJob: builder.mutation<{ message: string }, string>({
      query: (id) => ({
        url: `/jobs/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: [...JOB_PLACEMENT_TAGS],
    }),

    publishJob: builder.mutation<JobPosting, string>({
      query: (id) => ({
        url: `/jobs/${id}/publish`,
        method: "POST",
      }),
      invalidatesTags: (_result, _error, id) => ["Jobs", { type: "Jobs", id }],
    }),

    unpublishJob: builder.mutation<JobPosting, string>({
      query: (id) => ({
        url: `/jobs/${id}/unpublish`,
        method: "POST",
      }),
      invalidatesTags: (_result, _error, id) => ["Jobs", { type: "Jobs", id }],
    }),

    duplicateJob: builder.mutation<JobPosting, { id: string; includeQuestions?: boolean }>({
      query: ({ id, ...body }) => ({
        url: `/jobs/${id}/duplicate`,
        method: "POST",
        body,
      }),
      invalidatesTags: [...JOB_PLACEMENT_TAGS],
    }),

    getJobFilterOptions: builder.query<{ departments: string[]; locations: string[] }, void>({
      query: () => "/jobs/filter-options",
      providesTags: ["Jobs"],
    }),

    /** Whether new consent items start from the carrier-ready SMS wording (sms_compliance). */
    getConsentDefaults: builder.query<{ smsCompliance: boolean }, void>({
      query: () => "/jobs/consent-defaults",
    }),

    generateDescription: builder.mutation<
      { description: string },
      {
        title: string;
        department?: string;
        industry?: string;
        jobType?: string;
        location?: string;
        instructions?: string;
        seniority?: string;
        urgency?: string;
      }
    >({
      query: (body) => ({
        url: "/jobs/generate-description",
        method: "POST",
        body,
      }),
    }),

    generateScreeningQuestions: builder.mutation<
      {
        questions: Array<{
          questionText: string;
          questionType: "text" | "textarea" | "yes_no" | "select";
          options?: string[];
          isRequired: boolean;
        }>;
      },
      {
        title: string;
        description?: string;
        requirements?: string[];
        department?: string;
        jobType?: string;
        difficulty?: "easy" | "medium" | "hard";
        numberOfQuestions?: number;
        notes?: string;
      }
    >({
      query: (body) => ({
        url: "/jobs/generate-screening-questions",
        method: "POST",
        body,
      }),
    }),

    addJobFollower: builder.mutation<{ message: string }, { jobId: string; userEmail: string }>({
      query: ({ jobId, ...body }) => ({
        url: `/jobs/${jobId}/followers`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_result, _error, { jobId }) => [
        { type: "Jobs", id: jobId },
        { type: "JobFollowers", id: jobId },
      ],
    }),

    removeJobFollower: builder.mutation<{ message: string }, { jobId: string; email: string }>({
      query: ({ jobId, email }) => ({
        url: `/jobs/${jobId}/followers/${encodeURIComponent(email)}`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, { jobId }) => [
        { type: "Jobs", id: jobId },
        { type: "JobFollowers", id: jobId },
      ],
    }),

    getJobAccess: builder.query<JobAccessResponse, string>({
      query: (jobId) => `/jobs/${jobId}/access`,
      providesTags: (_result, _error, jobId) => [{ type: "JobAccess", id: jobId }],
    }),

    addJobAccessUser: builder.mutation<{ message: string }, { jobId: string; userEmail: string }>({
      query: ({ jobId, ...body }) => ({
        url: `/jobs/${jobId}/access/users`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_result, _error, { jobId }) => [{ type: "JobAccess", id: jobId }],
    }),

    removeJobAccessUser: builder.mutation<{ message: string }, { jobId: string; email: string }>({
      query: ({ jobId, email }) => ({
        url: `/jobs/${jobId}/access/users/${encodeURIComponent(email)}`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, { jobId }) => [{ type: "JobAccess", id: jobId }],
    }),

    // Who would see the job, now and after a move or a newly named person (Phase J2).
    getJobReach: builder.query<JobReachResponse, JobReachArgs>({
      query: ({ jobId, instanceId, email, locationId, departmentId }) => ({
        url: `/jobs/${jobId}/reach`,
        params: {
          instanceId,
          ...(email !== undefined ? { email } : {}),
          ...(locationId !== undefined ? { locationId } : {}),
          ...(departmentId !== undefined ? { departmentId } : {}),
        },
      }),
      // A saved change to the job (its place, its recruiter) changes the answer.
      providesTags: (_result, _error, { jobId }) => [{ type: "Jobs", id: jobId }],
    }),
  }),
});

export const {
  useGetJobsQuery,
  useLazyGetJobsQuery,
  useGetJobQuery,
  useCreateJobMutation,
  useUpdateJobMutation,
  useDeleteJobMutation,
  usePublishJobMutation,
  useUnpublishJobMutation,
  useDuplicateJobMutation,
  useGetJobFilterOptionsQuery,
  useGetConsentDefaultsQuery,
  useGenerateDescriptionMutation,
  useGenerateScreeningQuestionsMutation,
  useAddJobFollowerMutation,
  useRemoveJobFollowerMutation,
  useGetJobAccessQuery,
  useAddJobAccessUserMutation,
  useRemoveJobAccessUserMutation,
  useGetJobReachQuery,
} = jobsApi;
